import { useMount } from 'ahooks';
import { ReactNode, useState } from 'react';

type DeferredMountPhase = 'background' | 'content' | 'third-party';

const PHASE_DELAY_MS: Record<DeferredMountPhase, number> = {
  content: 0,
  background: 750,
  'third-party': 2000,
};

const phaseListeners: Record<DeferredMountPhase, Set<() => void>> = {
  content: new Set(),
  background: new Set(),
  'third-party': new Set(),
};
const releasedPhases = new Set<DeferredMountPhase>();
let hasStartedScheduler = false;

const releasePhase = (phase: DeferredMountPhase) => {
  const release = () => {
    releasedPhases.add(phase);
    phaseListeners[phase].forEach((listener) => listener());
    phaseListeners[phase].clear();
  };

  window.setTimeout(() => {
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(release, { timeout: 1000 });
      return;
    }
    release();
  }, PHASE_DELAY_MS[phase]);
};

const startScheduler = () => {
  if (hasStartedScheduler) return;
  hasStartedScheduler = true;

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      releasePhase('content');
      releasePhase('background');
      releasePhase('third-party');
    });
  });
};

const subscribeToPhase = (phase: DeferredMountPhase, listener: () => void) => {
  if (releasedPhases.has(phase)) {
    listener();
    return () => {};
  }

  phaseListeners[phase].add(listener);
  startScheduler();
  return () => phaseListeners[phase].delete(listener);
};

const DeferredMount = ({
  children,
  phase = 'content',
}: {
  children: ReactNode;
  phase?: DeferredMountPhase;
}) => {
  const [isReady, setIsReady] = useState(false);

  useMount(() => subscribeToPhase(phase, () => setIsReady(true)));

  return isReady ? children : null;
};

export default DeferredMount;
